import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

const TPinModal = ({
  show,
  onHide,
  onVerify,
  title = 'Enter T-PIN',
  message = 'Please enter your 4-digit T-PIN to continue',
  pinLength = 4,
  confirmLabel = 'Submit',
  requireAmount = false,
  amountLabel = 'Amount',
  amountPlaceholder = 'Enter amount',
  amountMin = 1,
  contextDetails = [],
  requireBankSelection = false,
  bankLabel = 'Select Bank',
  bankPlaceholder = 'Choose bank',
  bankOptions = [],
  defaultBank = ''
}) => {
  const [tpin, setTpin] = useState(new Array(pinLength).fill(''));
  const [amount, setAmount] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [bankError, setBankError] = useState('');
  const [isTpinVisible, setIsTpinVisible] = useState(false);
  const [isHoveringSubmit, setIsHoveringSubmit] = useState(false);
  const inputRefs = useRef([]);
  const amountInputRef = useRef(null);
  const bankSelectRef = useRef(null);

  const resolveOptionValue = (option) => {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object') {
      return option.value ?? option.label ?? '';
    }
    return '';
  };

  const resolveOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object') {
      return option.label ?? option.value ?? '';
    }
    return '';
  };

  // Reset on modal open
  useEffect(() => {
    if (show) {
      setTpin(new Array(pinLength).fill(''));
      setAmount('');
      setError('');
      setAmountError('');
      const defaultBankValue =
        defaultBank ||
        (bankOptions.length > 0 ? resolveOptionValue(bankOptions[0]) : '');
      setSelectedBank(defaultBankValue);
      setBankError('');
      setIsTpinVisible(false);
      setIsVerifying(false);
      setIsHoveringSubmit(false);
      // Focus first input
      setTimeout(() => {
        if (requireAmount && amountInputRef.current) {
          amountInputRef.current.focus();
        } else if (requireBankSelection && bankSelectRef.current) {
          bankSelectRef.current.focus();
        } else if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }
  }, [show, pinLength, requireAmount, requireBankSelection, defaultBank, bankOptions]);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return;

    const newTpin = [...tpin];
    newTpin[index] = element.value;
    setTpin(newTpin);
    setError('');

    // Move to next input
    if (element.value && index < pinLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Move to previous input on backspace
    if (e.key === 'Backspace' && !tpin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, pinLength);
    const newTpin = [...tpin];
    
    for (let i = 0; i < pastedData.length; i++) {
      if (!isNaN(pastedData[i])) {
        newTpin[i] = pastedData[i];
      }
    }
    
    setTpin(newTpin);
    
    // Focus last filled input or next empty
    const lastFilledIndex = Math.min(pastedData.length, pinLength - 1);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleVerify = async () => {
    if (requireAmount) {
      const amountValue = Number(amount);
      if (!amount || Number.isNaN(amountValue) || amountValue < amountMin) {
        setAmountError(`Enter a valid amount (min ${amountMin})`);
        if (!amountInputRef.current) return;
        amountInputRef.current.focus();
        return;
      }
    }

    if (requireBankSelection) {
      if (!selectedBank) {
        setBankError('Please select a bank');
        bankSelectRef.current?.focus();
        return;
      }
    }

    const tpinValue = tpin.join('');
    if (tpinValue.length !== pinLength) {
      setError('Please enter complete T-PIN');
      return;
    }

    setIsVerifying(true);
    setError('');
    
    try {
      const payload = {
        tpin: tpinValue,
        amount: requireAmount ? Number(amount) : undefined,
        bank: requireBankSelection ? selectedBank : undefined
      };
      await onVerify(payload);
    } catch (error) {
      setError(error.message || 'Invalid T-PIN. Please try again.');
      setTpin(new Array(pinLength).fill(''));
      if (requireAmount) {
        amountInputRef.current?.focus();
      } else {
        inputRefs.current[0]?.focus();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const isTpinComplete = tpin.every((digit) => digit !== '');
  const isAmountValid = !requireAmount || (!!amount && !Number.isNaN(Number(amount)) && Number(amount) >= amountMin);
  const isBankValid = !requireBankSelection || !!selectedBank;
  const isSubmitDisabled = !isTpinComplete || isVerifying || !isAmountValid || !isBankValid;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-key me-2" style={{ color: '#3b82f6' }}></i>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center py-4">
        <div className="mb-3">
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            {message}
          </p>
        </div>

        {Array.isArray(contextDetails) && contextDetails.length > 0 && (
          <div
            style={{
              textAlign: 'left',
              maxWidth: '360px',
              margin: '0 auto 1rem',
              fontSize: '0.9rem',
              color: '#1f2937'
            }}
          >
            <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Transfer Details
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {contextDetails.map(({ label, value }) => (
                <div key={label} style={{ minWidth: '150px', flex: '1 1 150px' }}>
                  <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9ca3af', marginBottom: '0.25rem' }}>
                    {label}
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {value || '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {requireAmount && (
          <Form.Group
            className="text-start"
            style={{ maxWidth: '360px', margin: '0 auto 0.75rem' }}
          >
            <Form.Label style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>
              {amountLabel} <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Control
              ref={amountInputRef}
              type="number"
              min={amountMin}
              value={amount}
              placeholder={amountPlaceholder}
              onChange={(e) => {
                setAmount(e.target.value);
                if (amountError) {
                  setAmountError('');
                }
              }}
              isInvalid={!!amountError}
              style={{
                padding: '0.45rem 0.9rem',
                fontSize: '0.95rem',
                borderRadius: '5px'
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  inputRefs.current[0]?.focus();
                }
              }}
            />
            {amountError && (
              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                {amountError}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        )}

        {requireBankSelection && (
          <Form.Group
            className="text-start"
            style={{ maxWidth: '360px', margin: '0 auto 0.75rem' }}
          >
            <Form.Label style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.9rem' }}>
              {bankLabel} <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Select
              ref={bankSelectRef}
              value={selectedBank}
              onChange={(e) => {
                setSelectedBank(e.target.value);
                if (bankError) {
                  setBankError('');
                }
              }}
              style={{
                padding: '0.45rem 0.9rem',
                fontSize: '0.95rem',
                borderRadius: '5px'
              }}
              isInvalid={!!bankError}
            >
              {!defaultBank && (
                <option value="">{bankPlaceholder}</option>
              )}
              {bankOptions.map((option) => {
                const value = resolveOptionValue(option);
                const label = resolveOptionLabel(option);
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </Form.Select>
            {bankError && (
              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                {bankError}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        )}

        {/* T-PIN Input Fields */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
            maxWidth: '360px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          <span
            style={{
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '0.9rem'
            }}
          >
            Enter T-PIN <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <button
            type="button"
            onClick={() => setIsTpinVisible((prev) => !prev)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#2563eb',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <i className={`bi ${isTpinVisible ? 'bi-eye-slash' : 'bi-eye'}`} style={{ fontSize: '1rem' }}></i>
            {isTpinVisible ? 'Hide' : 'Show'}
          </button>
        </div>
        <div
          className="d-flex justify-content-center gap-2 mb-3"
          style={{ maxWidth: '360px', margin: '0 auto' }}
        >
          {tpin.map((digit, index) => (
            <Form.Control
              key={index}
              ref={(ref) => (inputRefs.current[index] = ref)}
              type={isTpinVisible ? 'text' : 'password'}
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              style={{
                width: '52px',
                height: '46px',
                textAlign: 'center',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger py-2 mt-3" role="alert">
            <i className="bi bi-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={isVerifying}
          style={{
            padding: '0.5rem 1.5rem',
            fontSize: '0.9rem'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          disabled={isSubmitDisabled}
          onMouseEnter={() => {
            if (!isSubmitDisabled) {
              setIsHoveringSubmit(true);
            }
          }}
          onMouseLeave={() => setIsHoveringSubmit(false)}
          style={{
            padding: '0.5rem 1.5rem',
            fontSize: '0.9rem',
            backgroundColor: isSubmitDisabled ? '#f1f5f9' : isHoveringSubmit ? '#3b82f6' : 'transparent',
            color: isSubmitDisabled ? '#94a3b8' : isHoveringSubmit ? '#fff' : '#3b82f6',
            border: `2px solid ${isSubmitDisabled ? '#cbd5f5' : '#3b82f6'}`,
            fontWeight: '600',
            cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {isVerifying ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Verifying...
            </>
          ) : (
            <>
              <i className="bi bi-arrow-right-circle me-1"></i>
              {confirmLabel}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TPinModal;

